import { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { debounce, isEmpty, map } from "lodash";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faXmark,
  faSpinner,
} from "@fortawesome/pro-regular-svg-icons";
import LinkPreview, { Link } from "../../shared/LinkPreview";
import useLinkCrawler from "../../../hooks/useLinkCrawler";
import { extractAllUrls } from "../../../helpers/extractUrl";

type Props = {
  content: string;
  initialLink?: Link | null;
};

function scanContent(content: string): string[] {
  return extractAllUrls(content).filter((url) => URL.canParse(url));
}

// Encapsulates the body-text → preview-URL → crawl → link-preview pipeline.
// - Scans `content` (debounced) for URLs to populate the dropdown.
// - Keeps the saved seed URL in the dropdown even after the first edit,
//   so edits on API-seeded posts don't drop the current selection.
// - Guards against stale crawl responses when the user switches rapidly.
// - Writes crawl results into `form.link` via react-hook-form.
//
// Mount with `key={activity?.id ?? \`new-${instanceKey}\`}` from the parent to
// get clean re-initialization after successful creates (instanceKey bumps).
export default function LinkPreviewFromContent({
  content,
  initialLink,
}: Props) {
  const { watch, setValue, resetField, getValues } = useFormContext();
  const linkAttachment = watch("link") as Link | undefined;

  const initialPreviewUrl = initialLink?.url ?? null;

  const [availableUrls, setAvailableUrls] = useState<string[]>(() => {
    const fromContent = scanContent(content);
    if (!initialPreviewUrl) return fromContent;
    return fromContent.includes(initialPreviewUrl)
      ? fromContent
      : [initialPreviewUrl, ...fromContent];
  });
  const [selectedPreviewUrl, setSelectedPreviewUrl] = useState<string | null>(
    initialPreviewUrl,
  );

  const selectedPreviewUrlRef = useRef(selectedPreviewUrl);
  useEffect(() => {
    selectedPreviewUrlRef.current = selectedPreviewUrl;
  }, [selectedPreviewUrl]);

  const { mutate: crawlUrl, isLoading: isCrawling } = useLinkCrawler({
    onSuccess: (link: Link) => {
      if (link.url !== selectedPreviewUrlRef.current) return;
      setValue("link", link);
    },
  });

  const scanForUrls = useMemo(
    () =>
      debounce((next: string) => {
        const urls = scanContent(next);
        setAvailableUrls(
          initialPreviewUrl && !urls.includes(initialPreviewUrl)
            ? [initialPreviewUrl, ...urls]
            : urls,
        );
      }, 300),
    [initialPreviewUrl],
  );

  useEffect(() => {
    scanForUrls(content);
  }, [content, scanForUrls]);

  useEffect(() => {
    if (selectedPreviewUrl && availableUrls.includes(selectedPreviewUrl))
      return;
    const next = availableUrls[0] ?? null;
    if (next === selectedPreviewUrl) return;
    setSelectedPreviewUrl(next);
  }, [availableUrls, selectedPreviewUrl]);

  useEffect(() => {
    if (!selectedPreviewUrl) {
      resetField("link");
      return;
    }
    const currentLink = getValues("link") as Link | undefined;
    if (currentLink?.url === selectedPreviewUrl) return;
    crawlUrl(selectedPreviewUrl);
  }, [selectedPreviewUrl, crawlUrl, resetField, getValues]);

  if (!linkAttachment) return null;

  return (
    <div className="flex flex-col gap-2">
      {availableUrls.length > 1 && (
        <label className="flex flex-col gap-1">
          <span className="text-sm text-muted flex items-center gap-2">
            {I18n.t("js.activitystreams.composer.link_preview.select_url")}
            {isCrawling && (
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                aria-hidden="true"
                data-testid="link-preview-spinner"
              />
            )}
          </span>
          <select
            className="form-select"
            value={selectedPreviewUrl ?? ""}
            onChange={(e) => setSelectedPreviewUrl(e.target.value)}
          >
            {map(availableUrls, (url) => (
              <option key={url} value={url}>
                {url}
              </option>
            ))}
          </select>
        </label>
      )}
      <div
        aria-busy={isCrawling}
        className={classNames(
          "border-box grid items-center grid-cols-[auto_1fr_auto] transition-opacity",
          { "opacity-50": isCrawling },
        )}
      >
        <LinkPreview link={linkAttachment} />
        <button
          className="btn btn-sm self-start text-muted"
          type="button"
          onClick={(e) => {
            e.preventDefault();
            setValue("link", null);
          }}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
      {!isEmpty(linkAttachment.embed) && (
        <p className="flex items-center gap-1 text-sm text-muted">
          <FontAwesomeIcon icon={faCircleCheck} />
          {I18n.t("js.activitystreams.embed.composer_hint")}
        </p>
      )}
    </div>
  );
}
